import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateOrdersApplicationErrors,
  BulkUpdateOrdersOptions,
  BulkUpdateOrdersResponse,
  CaptureAuthorizedPaymentApplicationErrors,
  CaptureAuthorizedPaymentOptions,
  CheckoutOptionsForRequest,
  CheckoutResponse,
  ConfirmOrderApplicationErrors,
  ConfirmOrderOptions,
  ConfirmOrderResponse,
  CreateReservationOptions,
  CreateReservationResponse,
  GetCheckoutOptionsResponse,
  GetInvoiceOptions,
  GetInvoiceResponse,
  GetOrderIdentifiers,
  GetOrderOptions,
  GetSummaryOptions,
  GetSummaryResponse,
  ListAvailableTicketsOptions,
  ListAvailableTicketsResponse,
  ListOrdersOptions,
  ListOrdersResponse,
  Order,
  OrderConfirmedEnvelope,
  OrderDeletedEnvelope,
  OrderInitiatedEnvelope,
  OrderReservationCreatedEnvelope,
  OrderReservationUpdatedEnvelope,
  OrderUpdatedEnvelope,
  PosCheckoutOptions,
  PosCheckoutResponse,
  QueryAvailableTicketsOptions,
  QueryAvailableTicketsResponse,
  UpdateCheckoutOptions,
  UpdateCheckoutResponse,
  UpdateOrderApplicationErrors,
  UpdateOrderIdentifiers,
  UpdateOrderOptions,
  UpdateOrderResponse,
  VoidAuthorizedPaymentApplicationErrors,
  VoidAuthorizedPaymentOptions,
  bulkUpdateOrders as universalBulkUpdateOrders,
  cancelReservation as universalCancelReservation,
  captureAuthorizedPayment as universalCaptureAuthorizedPayment,
  checkout as universalCheckout,
  confirmOrder as universalConfirmOrder,
  createReservation as universalCreateReservation,
  getCheckoutOptions as universalGetCheckoutOptions,
  getInvoice as universalGetInvoice,
  getOrder as universalGetOrder,
  getSummary as universalGetSummary,
  listAvailableTickets as universalListAvailableTickets,
  listOrders as universalListOrders,
  posCheckout as universalPosCheckout,
  queryAvailableTickets as universalQueryAvailableTickets,
  updateCheckout as universalUpdateCheckout,
  updateOrder as universalUpdateOrder,
  voidAuthorizedPayment as universalVoidAuthorizedPayment,
} from './events-v1-order-orders.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function listOrders(httpClient: HttpClient): ListOrdersSignature {
  return (options?: ListOrdersOptions) =>
    universalListOrders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListOrdersSignature {
  /**
   * Retrieves a list of orders, including ticket data.
   * @param - An object representing the available options for retrieving a list of orders.
   */
  (options?: ListOrdersOptions): Promise<
    NonNullablePaths<
      ListOrdersResponse,
      | `total`
      | `offset`
      | `limit`
      | `orders`
      | `orders.${number}.orderNumber`
      | `orders.${number}.reservationId`
      | `orders.${number}.snapshotId`
      | `orders.${number}.eventId`
      | `orders.${number}.contactId`
      | `orders.${number}.memberId`
      | `orders.${number}.firstName`
      | `orders.${number}.lastName`
      | `orders.${number}.email`
      | `orders.${number}.confirmed`
      | `orders.${number}.status`
      | `orders.${number}.method`
      | `orders.${number}.ticketsQuantity`
      | `orders.${number}.totalPrice.amount`
      | `orders.${number}.totalPrice.currency`
      | `orders.${number}.ticketsPdf`
      | `orders.${number}.archived`
      | `orders.${number}.anonymized`
      | `orders.${number}.fullName`
      | `orders.${number}.invoice.discount.code`
      | `orders.${number}.invoice.discount.name`
      | `orders.${number}.invoice.discount.couponId`
      | `orders.${number}.invoice.tax.type`
      | `orders.${number}.invoice.tax.name`
      | `orders.${number}.invoice.tax.rate`
      | `orders.${number}.fullyCheckedIn`
      | `orders.${number}.paymentDetails.transaction.transactionId`
      | `orders.${number}.paymentDetails.transaction.method`
      | `orders.${number}.channel`,
      6
    >
  >;
}

export function getOrder(httpClient: HttpClient): GetOrderSignature {
  return (
    identifiers: NonNullablePaths<
      GetOrderIdentifiers,
      `eventId` | `orderNumber`,
      2
    >,
    options?: GetOrderOptions
  ) =>
    universalGetOrder(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOrderSignature {
  /**
   * Retrieves an order, including ticket data.
   * <!--
   * >The fieldsets in this function are restricted and only run if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - An object representing the available options for getting an order.
   * @param - An object containing identifiers for the order to be retrieved.
   * @returns Requested order.
   */
  (
    identifiers: NonNullablePaths<
      GetOrderIdentifiers,
      `eventId` | `orderNumber`,
      2
    >,
    options?: GetOrderOptions
  ): Promise<
    NonNullablePaths<
      Order,
      | `orderNumber`
      | `reservationId`
      | `snapshotId`
      | `eventId`
      | `contactId`
      | `memberId`
      | `firstName`
      | `lastName`
      | `email`
      | `checkoutForm.inputValues`
      | `checkoutForm.inputValues.${number}.inputName`
      | `checkoutForm.inputValues.${number}.value`
      | `confirmed`
      | `status`
      | `method`
      | `ticketsQuantity`
      | `totalPrice.amount`
      | `totalPrice.currency`
      | `ticketsPdf`
      | `tickets`
      | `tickets.${number}.ticketNumber`
      | `tickets.${number}.orderNumber`
      | `tickets.${number}.ticketDefinitionId`
      | `tickets.${number}.name`
      | `tickets.${number}.free`
      | `tickets.${number}.policy`
      | `tickets.${number}.orderStatus`
      | `tickets.${number}.orderArchived`
      | `tickets.${number}.orderFullName`
      | `tickets.${number}.guestDetails.guestAssigned`
      | `tickets.${number}.archived`
      | `tickets.${number}.anonymized`
      | `tickets.${number}.checkInUrl`
      | `tickets.${number}.ticketPdfUrl`
      | `tickets.${number}.channel`
      | `tickets.${number}.walletPassUrl`
      | `archived`
      | `anonymized`
      | `fullName`
      | `invoice.items`
      | `invoice.items.${number}._id`
      | `invoice.items.${number}.quantity`
      | `invoice.items.${number}.name`
      | `invoice.items.${number}.discount.code`
      | `invoice.items.${number}.discount.name`
      | `invoice.items.${number}.discount.couponId`
      | `invoice.items.${number}.tax.type`
      | `invoice.items.${number}.tax.name`
      | `invoice.items.${number}.tax.rate`
      | `invoice.fees`
      | `fullyCheckedIn`
      | `paymentDetails.transaction.transactionId`
      | `paymentDetails.transaction.method`
      | `channel`,
      6
    >
  >;
}

export function updateOrder(httpClient: HttpClient): UpdateOrderSignature {
  return (
    identifiers: NonNullablePaths<
      UpdateOrderIdentifiers,
      `eventId` | `orderNumber`,
      2
    >,
    options?: UpdateOrderOptions
  ) =>
    universalUpdateOrder(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateOrderSignature {
  /**
   * Updates an order.
   * @param - An object representing the available options for updating an order.
   * @param - An object containing identifiers for the order to be updated.
   */
  (
    identifiers: NonNullablePaths<
      UpdateOrderIdentifiers,
      `eventId` | `orderNumber`,
      2
    >,
    options?: UpdateOrderOptions
  ): Promise<
    NonNullablePaths<
      UpdateOrderResponse,
      | `order.orderNumber`
      | `order.reservationId`
      | `order.snapshotId`
      | `order.eventId`
      | `order.contactId`
      | `order.memberId`
      | `order.firstName`
      | `order.lastName`
      | `order.email`
      | `order.checkoutForm.inputValues`
      | `order.checkoutForm.inputValues.${number}.inputName`
      | `order.checkoutForm.inputValues.${number}.value`
      | `order.confirmed`
      | `order.status`
      | `order.method`
      | `order.ticketsQuantity`
      | `order.totalPrice.amount`
      | `order.totalPrice.currency`
      | `order.ticketsPdf`
      | `order.tickets`
      | `order.tickets.${number}.ticketNumber`
      | `order.tickets.${number}.orderNumber`
      | `order.tickets.${number}.ticketDefinitionId`
      | `order.tickets.${number}.name`
      | `order.tickets.${number}.free`
      | `order.tickets.${number}.policy`
      | `order.tickets.${number}.orderStatus`
      | `order.tickets.${number}.orderArchived`
      | `order.tickets.${number}.orderFullName`
      | `order.tickets.${number}.guestDetails.guestAssigned`
      | `order.tickets.${number}.archived`
      | `order.tickets.${number}.anonymized`
      | `order.tickets.${number}.checkInUrl`
      | `order.tickets.${number}.ticketPdfUrl`
      | `order.tickets.${number}.channel`
      | `order.tickets.${number}.walletPassUrl`
      | `order.archived`
      | `order.anonymized`
      | `order.fullName`
      | `order.invoice.items`
      | `order.invoice.items.${number}._id`
      | `order.invoice.items.${number}.quantity`
      | `order.invoice.items.${number}.name`
      | `order.invoice.items.${number}.discount.code`
      | `order.invoice.items.${number}.discount.name`
      | `order.invoice.items.${number}.discount.couponId`
      | `order.invoice.items.${number}.tax.type`
      | `order.invoice.items.${number}.tax.name`
      | `order.invoice.items.${number}.tax.rate`
      | `order.invoice.fees`
      | `order.fullyCheckedIn`
      | `order.paymentDetails.transaction.transactionId`
      | `order.paymentDetails.transaction.method`
      | `order.channel`,
      7
    > & {
      __applicationErrorsType?: UpdateOrderApplicationErrors;
    }
  >;
}

export function bulkUpdateOrders(
  httpClient: HttpClient
): BulkUpdateOrdersSignature {
  return (eventId: string, options?: BulkUpdateOrdersOptions) =>
    universalBulkUpdateOrders(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOrdersSignature {
  /**
   * Archives multiple orders.
   * @param - An object representing the available options for confirming an order.
   * @param - Event ID to which the order belongs.
   */
  (eventId: string, options?: BulkUpdateOrdersOptions): Promise<
    NonNullablePaths<
      BulkUpdateOrdersResponse,
      | `orders`
      | `orders.${number}.orderNumber`
      | `orders.${number}.reservationId`
      | `orders.${number}.snapshotId`
      | `orders.${number}.eventId`
      | `orders.${number}.contactId`
      | `orders.${number}.memberId`
      | `orders.${number}.firstName`
      | `orders.${number}.lastName`
      | `orders.${number}.email`
      | `orders.${number}.confirmed`
      | `orders.${number}.status`
      | `orders.${number}.method`
      | `orders.${number}.ticketsQuantity`
      | `orders.${number}.totalPrice.amount`
      | `orders.${number}.totalPrice.currency`
      | `orders.${number}.ticketsPdf`
      | `orders.${number}.archived`
      | `orders.${number}.anonymized`
      | `orders.${number}.fullName`
      | `orders.${number}.invoice.discount.code`
      | `orders.${number}.invoice.discount.name`
      | `orders.${number}.invoice.discount.couponId`
      | `orders.${number}.invoice.tax.type`
      | `orders.${number}.invoice.tax.name`
      | `orders.${number}.invoice.tax.rate`
      | `orders.${number}.fullyCheckedIn`
      | `orders.${number}.paymentDetails.transaction.transactionId`
      | `orders.${number}.paymentDetails.transaction.method`
      | `orders.${number}.channel`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateOrdersApplicationErrors;
    }
  >;
}

export function confirmOrder(httpClient: HttpClient): ConfirmOrderSignature {
  return (eventId: string, options?: ConfirmOrderOptions) =>
    universalConfirmOrder(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ConfirmOrderSignature {
  /**
   * Confirms an order.
   *
   *
   * This function changes order status from `INITIATED`, `PENDING`, `OFFLINE_PENDING` to `PAID`.
   * Confirming orders with `INITIATED` or `PENDING` status triggers an email with the tickets to the buyer (and to additional guests, if provided).
   * @param - An object representing the available options for confirming an order.
   * @param - Event ID to which the order belongs.
   */
  (eventId: string, options?: ConfirmOrderOptions): Promise<
    NonNullablePaths<
      ConfirmOrderResponse,
      | `orders`
      | `orders.${number}.orderNumber`
      | `orders.${number}.reservationId`
      | `orders.${number}.snapshotId`
      | `orders.${number}.eventId`
      | `orders.${number}.contactId`
      | `orders.${number}.memberId`
      | `orders.${number}.firstName`
      | `orders.${number}.lastName`
      | `orders.${number}.email`
      | `orders.${number}.confirmed`
      | `orders.${number}.status`
      | `orders.${number}.method`
      | `orders.${number}.ticketsQuantity`
      | `orders.${number}.totalPrice.amount`
      | `orders.${number}.totalPrice.currency`
      | `orders.${number}.ticketsPdf`
      | `orders.${number}.archived`
      | `orders.${number}.anonymized`
      | `orders.${number}.fullName`
      | `orders.${number}.invoice.discount.code`
      | `orders.${number}.invoice.discount.name`
      | `orders.${number}.invoice.discount.couponId`
      | `orders.${number}.invoice.tax.type`
      | `orders.${number}.invoice.tax.name`
      | `orders.${number}.invoice.tax.rate`
      | `orders.${number}.fullyCheckedIn`
      | `orders.${number}.paymentDetails.transaction.transactionId`
      | `orders.${number}.paymentDetails.transaction.method`
      | `orders.${number}.channel`,
      6
    > & {
      __applicationErrorsType?: ConfirmOrderApplicationErrors;
    }
  >;
}

export function getSummary(httpClient: HttpClient): GetSummarySignature {
  return (options?: GetSummaryOptions) =>
    universalGetSummary(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSummarySignature {
  /**
   * Retrieves a summary of total ticket sales.
   * <!--
   * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - An object representing the available options for retrieving a summary of total ticket sales.
   */
  (options?: GetSummaryOptions): Promise<
    NonNullablePaths<
      GetSummaryResponse,
      | `sales`
      | `sales.${number}.total.amount`
      | `sales.${number}.total.currency`
      | `sales.${number}.totalOrders`
      | `sales.${number}.totalTickets`,
      5
    >
  >;
}

export function captureAuthorizedPayment(
  httpClient: HttpClient
): CaptureAuthorizedPaymentSignature {
  return (orderNumber: string, options?: CaptureAuthorizedPaymentOptions) =>
    universalCaptureAuthorizedPayment(
      orderNumber,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CaptureAuthorizedPaymentSignature {
  /**
   * Captures authorized payment asynchronously.
   * Eventually order will become paid.
   * For orders with non-authorized payments request will fail.
   * @param - Order number.
   */
  (orderNumber: string, options?: CaptureAuthorizedPaymentOptions): Promise<
    void & {
      __applicationErrorsType?: CaptureAuthorizedPaymentApplicationErrors;
    }
  >;
}

export function voidAuthorizedPayment(
  httpClient: HttpClient
): VoidAuthorizedPaymentSignature {
  return (orderNumber: string, options?: VoidAuthorizedPaymentOptions) =>
    universalVoidAuthorizedPayment(
      orderNumber,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface VoidAuthorizedPaymentSignature {
  /**
   * Voids authorized payment asynchronously.
   * Eventually order will become voided.
   * For orders with non-authorized payments request will fail.
   * @param - Order number.
   */
  (orderNumber: string, options?: VoidAuthorizedPaymentOptions): Promise<
    void & {
      __applicationErrorsType?: VoidAuthorizedPaymentApplicationErrors;
    }
  >;
}

export function getCheckoutOptions(
  httpClient: HttpClient
): GetCheckoutOptionsSignature {
  return () =>
    universalGetCheckoutOptions(
      // @ts-ignore
      { httpClient }
    );
}

interface GetCheckoutOptionsSignature {
  /**
   * Retrieves checkout details.
   */
  (): Promise<
    NonNullablePaths<
      GetCheckoutOptionsResponse,
      | `paymentMethodConfigured`
      | `acceptCoupons`
      | `premiumServices`
      | `paidTickets`
      | `acceptGiftCards`,
      2
    >
  >;
}

export function listAvailableTickets(
  httpClient: HttpClient
): ListAvailableTicketsSignature {
  return (options?: ListAvailableTicketsOptions) =>
    universalListAvailableTickets(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAvailableTicketsSignature {
  /**
   * Returns tickets available to reserve.
   * <!--
   * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - An object representing the available options for retrieving a list of tickets available for reservation.
   */
  (options?: ListAvailableTicketsOptions): Promise<
    NonNullablePaths<
      ListAvailableTicketsResponse,
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`
      | `definitions`
      | `definitions.${number}._id`
      | `definitions.${number}.price.amount`
      | `definitions.${number}.price.currency`
      | `definitions.${number}.free`
      | `definitions.${number}.name`
      | `definitions.${number}.description`
      | `definitions.${number}.limitPerCheckout`
      | `definitions.${number}.orderIndex`
      | `definitions.${number}.policy`
      | `definitions.${number}.dashboard.hidden`
      | `definitions.${number}.dashboard.sold`
      | `definitions.${number}.dashboard.limited`
      | `definitions.${number}.dashboard.ticketsSold`
      | `definitions.${number}.dashboard.ticketsReserved`
      | `definitions.${number}.eventId`
      | `definitions.${number}.wixFeeConfig.type`
      | `definitions.${number}.salePeriod.hideNotOnSale`
      | `definitions.${number}.saleStatus`
      | `definitions.${number}.pricing.pricingType`,
      5
    >
  >;
}

export function queryAvailableTickets(
  httpClient: HttpClient
): QueryAvailableTicketsSignature {
  return (options?: QueryAvailableTicketsOptions) =>
    universalQueryAvailableTickets(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAvailableTicketsSignature {
  /**
   * Returns tickets available to reserve.
   * <!--
   * > Note: The fieldsets in this function are restricted and only run if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - An object representing the available options for retrieving a list of tickets available for reservation.
   */
  (options?: QueryAvailableTicketsOptions): Promise<
    NonNullablePaths<
      QueryAvailableTicketsResponse,
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`
      | `definitions`
      | `definitions.${number}._id`
      | `definitions.${number}.price.amount`
      | `definitions.${number}.price.currency`
      | `definitions.${number}.free`
      | `definitions.${number}.name`
      | `definitions.${number}.description`
      | `definitions.${number}.limitPerCheckout`
      | `definitions.${number}.orderIndex`
      | `definitions.${number}.policy`
      | `definitions.${number}.dashboard.hidden`
      | `definitions.${number}.dashboard.sold`
      | `definitions.${number}.dashboard.limited`
      | `definitions.${number}.dashboard.ticketsSold`
      | `definitions.${number}.dashboard.ticketsReserved`
      | `definitions.${number}.eventId`
      | `definitions.${number}.wixFeeConfig.type`
      | `definitions.${number}.salePeriod.hideNotOnSale`
      | `definitions.${number}.saleStatus`
      | `definitions.${number}.pricing.pricingType`,
      5
    >
  >;
}

export function createReservation(
  httpClient: HttpClient
): CreateReservationSignature {
  return (eventId: string, options?: CreateReservationOptions) =>
    universalCreateReservation(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateReservationSignature {
  /**
   * Reserves tickets for 20 minutes.
   *
   *
   * Reserved tickets are deducted from ticket stock and cannot be bought by another site visitor.
   * When the reservation expires, the tickets are added back to the stock.
   * @param - An object representing the available options for creating a reservation.
   * @param - Event ID to which the reservation belongs.
   * @deprecated
   */
  (eventId: string, options?: CreateReservationOptions): Promise<
    NonNullablePaths<
      CreateReservationResponse,
      | `_id`
      | `reservations`
      | `reservations.${number}.quantity`
      | `reservations.${number}.ticket._id`
      | `reservations.${number}.ticket.price.amount`
      | `reservations.${number}.ticket.price.currency`
      | `reservations.${number}.ticket.free`
      | `reservations.${number}.ticket.name`
      | `reservations.${number}.ticket.description`
      | `reservations.${number}.ticket.limitPerCheckout`
      | `reservations.${number}.ticket.orderIndex`
      | `reservations.${number}.ticket.policy`
      | `reservations.${number}.ticket.dashboard.hidden`
      | `reservations.${number}.ticket.dashboard.sold`
      | `reservations.${number}.ticket.dashboard.limited`
      | `reservations.${number}.ticket.dashboard.ticketsSold`
      | `reservations.${number}.ticket.dashboard.ticketsReserved`
      | `reservations.${number}.ticket.eventId`
      | `reservations.${number}.ticket.wixFeeConfig.type`
      | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
      | `reservations.${number}.ticket.saleStatus`
      | `reservations.${number}.ticket.pricing.pricingType`
      | `invoice.items`
      | `invoice.items.${number}._id`
      | `invoice.items.${number}.quantity`
      | `invoice.items.${number}.name`
      | `invoice.items.${number}.price.amount`
      | `invoice.items.${number}.price.currency`
      | `invoice.items.${number}.discount.code`
      | `invoice.items.${number}.discount.name`
      | `invoice.items.${number}.discount.couponId`
      | `invoice.items.${number}.tax.type`
      | `invoice.items.${number}.tax.name`
      | `invoice.items.${number}.tax.rate`
      | `invoice.fees`
      | `reservationStatus`,
      6
    >
  >;
}

export function cancelReservation(
  httpClient: HttpClient
): CancelReservationSignature {
  return (_id: string, eventId: string) =>
    universalCancelReservation(
      _id,
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelReservationSignature {
  /**
   * Cancels ticket reservation and returns tickets to stock.
   * <!--
   * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - Reservation ID.
   * @param - An object containing identifiers for the reservation to be cancelled.
   * @param - Event ID to which the reservation belongs.
   * @deprecated
   */
  (_id: string, eventId: string): Promise<void>;
}

export function getInvoice(httpClient: HttpClient): GetInvoiceSignature {
  return (
    reservationId: string,
    eventId: string,
    options?: GetInvoiceOptions
  ) =>
    universalGetInvoice(
      reservationId,
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetInvoiceSignature {
  /**
   * Generates a preview of an invoice, including the given coupon or pricing plan.
   * @param - Reservation ID.
   * @param - An object representing the available options for generating a preview of a reservation invoice.
   * @param - An object containing identifiers for the reservation invoice preview to be generated.
   * @param - Event ID to which the invoice belongs.
   */
  (
    reservationId: string,
    eventId: string,
    options?: GetInvoiceOptions
  ): Promise<
    NonNullablePaths<
      GetInvoiceResponse,
      | `invoice.items`
      | `invoice.items.${number}._id`
      | `invoice.items.${number}.quantity`
      | `invoice.items.${number}.name`
      | `invoice.items.${number}.price.amount`
      | `invoice.items.${number}.price.currency`
      | `invoice.items.${number}.discount.code`
      | `invoice.items.${number}.discount.name`
      | `invoice.items.${number}.discount.couponId`
      | `invoice.items.${number}.tax.type`
      | `invoice.items.${number}.tax.name`
      | `invoice.items.${number}.tax.rate`
      | `invoice.fees`
      | `discountErrors.error`
      | `discountErrors.error.${number}.code`
      | `reservationStatus`
      | `reservationOccupied`
      | `reservations`
      | `reservations.${number}.quantity`
      | `reservations.${number}.ticket._id`
      | `reservations.${number}.ticket.price.amount`
      | `reservations.${number}.ticket.price.currency`
      | `reservations.${number}.ticket.free`
      | `reservations.${number}.ticket.name`
      | `reservations.${number}.ticket.description`
      | `reservations.${number}.ticket.limitPerCheckout`
      | `reservations.${number}.ticket.orderIndex`
      | `reservations.${number}.ticket.policy`
      | `reservations.${number}.ticket.dashboard.hidden`
      | `reservations.${number}.ticket.dashboard.sold`
      | `reservations.${number}.ticket.dashboard.limited`
      | `reservations.${number}.ticket.dashboard.ticketsSold`
      | `reservations.${number}.ticket.dashboard.ticketsReserved`
      | `reservations.${number}.ticket.eventId`
      | `reservations.${number}.ticket.wixFeeConfig.type`
      | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
      | `reservations.${number}.ticket.saleStatus`
      | `reservations.${number}.ticket.pricing.pricingType`,
      6
    >
  >;
}

export function checkout(httpClient: HttpClient): CheckoutSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<
      CheckoutOptionsForRequest,
      `guests.${number}.form`,
      4
    >
  ) =>
    universalCheckout(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CheckoutSignature {
  /**
   * Checkouts the reserved tickets.
   *
   *
   * Creates an order and associates it with a site visitor contact.
   * Guest details are received from the [Registration Form](https://www.wix.com/velo/reference/wix-events-v2/forms/introduction) input fields.
   *
   * There is a possibility to use a separate ready-made Wix checkout form where the user will be redirected from your non-Wix site or a custom ticket picker created with Velo.
   * To build the checkout form path, get your event base URL by using the [`getEvent()`](https://www.wix.com/velo/reference/wix-events-backend/wixevents/getevent) function and add the following path:
   * `/{{EVENT_PAGE_SLUG}}/{{SLUG}}/ticket-form?reservationId={{YOUR_RESERVATION_ID}}`
   *
   * Example:  `https://johndoe.wixsite.com/weddings/event-details/doe-wedding/ticket-form?reservationId=2be6d34a-2a1e-459f-897b-b4a66e73f69a`
   * @param - An object representing the available options for checking out a reserved ticket.
   * @param - Event ID to which the checkout belongs.
   */
  (
    eventId: string,
    options?: NonNullablePaths<
      CheckoutOptionsForRequest,
      `guests.${number}.form`,
      4
    >
  ): Promise<
    NonNullablePaths<
      CheckoutResponse,
      | `order.orderNumber`
      | `order.reservationId`
      | `order.snapshotId`
      | `order.eventId`
      | `order.contactId`
      | `order.memberId`
      | `order.firstName`
      | `order.lastName`
      | `order.email`
      | `order.checkoutForm.inputValues`
      | `order.checkoutForm.inputValues.${number}.inputName`
      | `order.checkoutForm.inputValues.${number}.value`
      | `order.confirmed`
      | `order.status`
      | `order.method`
      | `order.ticketsQuantity`
      | `order.totalPrice.amount`
      | `order.totalPrice.currency`
      | `order.ticketsPdf`
      | `order.tickets`
      | `order.tickets.${number}.ticketNumber`
      | `order.tickets.${number}.orderNumber`
      | `order.tickets.${number}.ticketDefinitionId`
      | `order.tickets.${number}.name`
      | `order.tickets.${number}.free`
      | `order.tickets.${number}.policy`
      | `order.tickets.${number}.orderStatus`
      | `order.tickets.${number}.orderArchived`
      | `order.tickets.${number}.orderFullName`
      | `order.tickets.${number}.guestDetails.guestAssigned`
      | `order.tickets.${number}.archived`
      | `order.tickets.${number}.anonymized`
      | `order.tickets.${number}.checkInUrl`
      | `order.tickets.${number}.ticketPdfUrl`
      | `order.tickets.${number}.channel`
      | `order.tickets.${number}.walletPassUrl`
      | `order.archived`
      | `order.anonymized`
      | `order.fullName`
      | `order.invoice.items`
      | `order.invoice.items.${number}._id`
      | `order.invoice.items.${number}.quantity`
      | `order.invoice.items.${number}.name`
      | `order.invoice.items.${number}.discount.code`
      | `order.invoice.items.${number}.discount.name`
      | `order.invoice.items.${number}.discount.couponId`
      | `order.invoice.items.${number}.tax.type`
      | `order.invoice.items.${number}.tax.name`
      | `order.invoice.items.${number}.tax.rate`
      | `order.invoice.fees`
      | `order.fullyCheckedIn`
      | `order.paymentDetails.transaction.transactionId`
      | `order.paymentDetails.transaction.method`
      | `order.channel`
      | `reservations`
      | `reservations.${number}.quantity`
      | `reservations.${number}.ticket._id`
      | `reservations.${number}.ticket.price.amount`
      | `reservations.${number}.ticket.price.currency`
      | `reservations.${number}.ticket.free`
      | `reservations.${number}.ticket.name`
      | `reservations.${number}.ticket.description`
      | `reservations.${number}.ticket.limitPerCheckout`
      | `reservations.${number}.ticket.orderIndex`
      | `reservations.${number}.ticket.policy`
      | `reservations.${number}.ticket.dashboard.hidden`
      | `reservations.${number}.ticket.dashboard.sold`
      | `reservations.${number}.ticket.dashboard.limited`
      | `reservations.${number}.ticket.dashboard.ticketsSold`
      | `reservations.${number}.ticket.dashboard.ticketsReserved`
      | `reservations.${number}.ticket.eventId`
      | `reservations.${number}.ticket.wixFeeConfig.type`
      | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
      | `reservations.${number}.ticket.saleStatus`
      | `reservations.${number}.ticket.pricing.pricingType`,
      7
    >
  >;
}

export function updateCheckout(
  httpClient: HttpClient
): UpdateCheckoutSignature {
  return (
    orderNumber: string,
    eventId: string,
    options?: UpdateCheckoutOptions
  ) =>
    universalUpdateCheckout(
      orderNumber,
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCheckoutSignature {
  /**
   * Updates order and tickets.
   *
   *
   * Only applicable for orders with `INITIATED`, `PENDING`, `OFFLINE_PENDING` statuses.
   * @param - Unique order number.
   * @param - An object representing the available options for updating an order and tickets.
   * @param - An object containing identifiers for the order and tickets to be updated.
   * @param - Event ID to which the checkout belongs.
   */
  (
    orderNumber: string,
    eventId: string,
    options?: UpdateCheckoutOptions
  ): Promise<
    NonNullablePaths<
      UpdateCheckoutResponse,
      | `order.orderNumber`
      | `order.reservationId`
      | `order.snapshotId`
      | `order.eventId`
      | `order.contactId`
      | `order.memberId`
      | `order.firstName`
      | `order.lastName`
      | `order.email`
      | `order.checkoutForm.inputValues`
      | `order.checkoutForm.inputValues.${number}.inputName`
      | `order.checkoutForm.inputValues.${number}.value`
      | `order.confirmed`
      | `order.status`
      | `order.method`
      | `order.ticketsQuantity`
      | `order.totalPrice.amount`
      | `order.totalPrice.currency`
      | `order.ticketsPdf`
      | `order.tickets`
      | `order.tickets.${number}.ticketNumber`
      | `order.tickets.${number}.orderNumber`
      | `order.tickets.${number}.ticketDefinitionId`
      | `order.tickets.${number}.name`
      | `order.tickets.${number}.free`
      | `order.tickets.${number}.policy`
      | `order.tickets.${number}.orderStatus`
      | `order.tickets.${number}.orderArchived`
      | `order.tickets.${number}.orderFullName`
      | `order.tickets.${number}.guestDetails.guestAssigned`
      | `order.tickets.${number}.archived`
      | `order.tickets.${number}.anonymized`
      | `order.tickets.${number}.checkInUrl`
      | `order.tickets.${number}.ticketPdfUrl`
      | `order.tickets.${number}.channel`
      | `order.tickets.${number}.walletPassUrl`
      | `order.archived`
      | `order.anonymized`
      | `order.fullName`
      | `order.invoice.items`
      | `order.invoice.items.${number}._id`
      | `order.invoice.items.${number}.quantity`
      | `order.invoice.items.${number}.name`
      | `order.invoice.items.${number}.discount.code`
      | `order.invoice.items.${number}.discount.name`
      | `order.invoice.items.${number}.discount.couponId`
      | `order.invoice.items.${number}.tax.type`
      | `order.invoice.items.${number}.tax.name`
      | `order.invoice.items.${number}.tax.rate`
      | `order.invoice.fees`
      | `order.fullyCheckedIn`
      | `order.paymentDetails.transaction.transactionId`
      | `order.paymentDetails.transaction.method`
      | `order.channel`,
      7
    >
  >;
}

export function posCheckout(httpClient: HttpClient): PosCheckoutSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<PosCheckoutOptions, `reservationId`, 2>
  ) =>
    universalPosCheckout(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PosCheckoutSignature {
  /**
   * Creates order with payment details already initiated via Cashier Pay API.
   * @param - Event ID to which the checkout belongs.
   */
  (
    eventId: string,
    options?: NonNullablePaths<PosCheckoutOptions, `reservationId`, 2>
  ): Promise<
    NonNullablePaths<
      PosCheckoutResponse,
      | `order.orderNumber`
      | `order.reservationId`
      | `order.snapshotId`
      | `order.eventId`
      | `order.contactId`
      | `order.memberId`
      | `order.firstName`
      | `order.lastName`
      | `order.email`
      | `order.checkoutForm.inputValues`
      | `order.checkoutForm.inputValues.${number}.inputName`
      | `order.checkoutForm.inputValues.${number}.value`
      | `order.confirmed`
      | `order.status`
      | `order.method`
      | `order.ticketsQuantity`
      | `order.totalPrice.amount`
      | `order.totalPrice.currency`
      | `order.ticketsPdf`
      | `order.tickets`
      | `order.tickets.${number}.ticketNumber`
      | `order.tickets.${number}.orderNumber`
      | `order.tickets.${number}.ticketDefinitionId`
      | `order.tickets.${number}.name`
      | `order.tickets.${number}.free`
      | `order.tickets.${number}.policy`
      | `order.tickets.${number}.orderStatus`
      | `order.tickets.${number}.orderArchived`
      | `order.tickets.${number}.orderFullName`
      | `order.tickets.${number}.guestDetails.guestAssigned`
      | `order.tickets.${number}.archived`
      | `order.tickets.${number}.anonymized`
      | `order.tickets.${number}.checkInUrl`
      | `order.tickets.${number}.ticketPdfUrl`
      | `order.tickets.${number}.channel`
      | `order.tickets.${number}.walletPassUrl`
      | `order.archived`
      | `order.anonymized`
      | `order.fullName`
      | `order.invoice.items`
      | `order.invoice.items.${number}._id`
      | `order.invoice.items.${number}.quantity`
      | `order.invoice.items.${number}.name`
      | `order.invoice.items.${number}.discount.code`
      | `order.invoice.items.${number}.discount.name`
      | `order.invoice.items.${number}.discount.couponId`
      | `order.invoice.items.${number}.tax.type`
      | `order.invoice.items.${number}.tax.name`
      | `order.invoice.items.${number}.tax.rate`
      | `order.invoice.fees`
      | `order.fullyCheckedIn`
      | `order.paymentDetails.transaction.transactionId`
      | `order.paymentDetails.transaction.method`
      | `order.channel`
      | `reservations`
      | `reservations.${number}.quantity`
      | `reservations.${number}.ticket._id`
      | `reservations.${number}.ticket.price.amount`
      | `reservations.${number}.ticket.price.currency`
      | `reservations.${number}.ticket.free`
      | `reservations.${number}.ticket.name`
      | `reservations.${number}.ticket.description`
      | `reservations.${number}.ticket.limitPerCheckout`
      | `reservations.${number}.ticket.orderIndex`
      | `reservations.${number}.ticket.policy`
      | `reservations.${number}.ticket.dashboard.hidden`
      | `reservations.${number}.ticket.dashboard.sold`
      | `reservations.${number}.ticket.dashboard.limited`
      | `reservations.${number}.ticket.dashboard.ticketsSold`
      | `reservations.${number}.ticket.dashboard.ticketsReserved`
      | `reservations.${number}.ticket.eventId`
      | `reservations.${number}.ticket.wixFeeConfig.type`
      | `reservations.${number}.ticket.salePeriod.hideNotOnSale`
      | `reservations.${number}.ticket.saleStatus`
      | `reservations.${number}.ticket.pricing.pricingType`,
      7
    >
  >;
}

export const onOrderDeleted = EventDefinition(
  'wix.events.ticketing.events.OrderDeleted',
  false,
  (event: OrderDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.updated' },
            { path: 'data.tickets.checkIn.created' },
            { path: 'data.tickets.checkIn.checkInCanceledDate' },
            { path: 'data.tickets.form.inputValues.dateTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.tickets.form.inputValues.number' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.tickets.form.inputValues.address.address' }],
        },
      ])
    )
)<OrderDeletedEnvelope>();
export const onOrderUpdated = EventDefinition(
  'wix.events.ticketing.events.OrderUpdated',
  false,
  (event: OrderUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.updated' },
            { path: 'data.checkoutForm.inputValues.dateTime' },
            { path: 'data.tickets.checkIn.created' },
            { path: 'data.tickets.checkIn.checkInCanceledDate' },
            { path: 'data.tickets.form.inputValues.dateTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.checkoutForm.inputValues.number' },
            { path: 'data.tickets.form.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.checkoutForm.inputValues.address.address' },
            { path: 'data.tickets.form.inputValues.address.address' },
          ],
        },
      ])
    )
)<OrderUpdatedEnvelope>();
export const onOrderConfirmed = EventDefinition(
  'wix.events.ticketing.events.OrderConfirmed',
  false,
  (event: OrderConfirmedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.updated' },
            { path: 'data.checkoutForm.inputValues.dateTime' },
            { path: 'data.tickets.checkIn.created' },
            { path: 'data.tickets.checkIn.checkInCanceledDate' },
            { path: 'data.tickets.form.inputValues.dateTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.checkoutForm.inputValues.number' },
            { path: 'data.tickets.form.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.checkoutForm.inputValues.address.address' },
            { path: 'data.tickets.form.inputValues.address.address' },
          ],
        },
      ])
    )
)<OrderConfirmedEnvelope>();
export const onOrderInitiated = EventDefinition(
  'wix.events.ticketing.events.OrderInitiated',
  false,
  (event: OrderInitiatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.updated' },
            { path: 'data.checkoutForm.inputValues.dateTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.checkoutForm.inputValues.number' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.checkoutForm.inputValues.address.address' }],
        },
      ])
    )
)<OrderInitiatedEnvelope>();
export const onOrderReservationCreated = EventDefinition(
  'wix.events.ticketing.events.ReservationCreated',
  false,
  (event: OrderReservationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.expires' },
            { path: 'data.updatedDate' },
            { path: 'data.quantities.updatedDate' },
            { path: 'data.counts.timestamp' },
          ],
        },
      ])
    )
)<OrderReservationCreatedEnvelope>();
export const onOrderReservationUpdated = EventDefinition(
  'wix.events.ticketing.events.ReservationUpdated',
  false,
  (event: OrderReservationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.expires' },
            { path: 'data.updatedDate' },
            { path: 'data.quantities.updatedDate' },
            { path: 'data.counts.timestamp' },
          ],
        },
      ])
    )
)<OrderReservationUpdatedEnvelope>();

export {
  AccountInfo,
  Action,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BalanceSummary,
  BaseEventMetadata,
  BulkUpdateOrdersOptions,
  BulkUpdateOrdersRequest,
  BulkUpdateOrdersResponse,
  Buyer,
  CalendarLinks,
  CancelReservationRequest,
  CancelReservationResponse,
  CaptureAuthorizedPaymentOptions,
  CaptureAuthorizedPaymentRequest,
  CaptureAuthorizedPaymentResponse,
  ChannelType,
  CheckIn,
  CheckoutOptions,
  CheckoutOptionsForRequest,
  CheckoutRequest,
  CheckoutResponse,
  ConfirmOrderOptions,
  ConfirmOrderRequest,
  ConfirmOrderResponse,
  ConfirmReservationRequest,
  ConfirmReservationResponse,
  Counts,
  CouponDiscount,
  CreateReservationOptions,
  CreateReservationRequest,
  CreateReservationResponse,
  Dashboard,
  Discount,
  DiscountErrors,
  DiscountItem,
  DiscountItemDiscountOneOf,
  DiscountRequest,
  Error,
  EventKey,
  ExpireReservationRequest,
  ExpireReservationResponse,
  FacetCounts,
  Fee,
  FeeName,
  FeeType,
  FindOrderByReservationIdRequest,
  FindOrderByReservationIdResponse,
  FormResponse,
  FormSubmission,
  FormattedAddress,
  GetCheckoutOptionsRequest,
  GetCheckoutOptionsResponse,
  GetInvoiceOptions,
  GetInvoicePreviewRequest,
  GetInvoiceRequest,
  GetInvoiceResponse,
  GetOrderIdentifiers,
  GetOrderOptions,
  GetOrderRequest,
  GetOrderResponse,
  GetPaymentInfoRequest,
  GetPaymentInfoResponse,
  GetReservationRequest,
  GetReservationResponse,
  GetSummaryOptions,
  GetSummaryRequest,
  GetSummaryResponse,
  GiftCardErrors,
  GiftCardErrorsError,
  GiftCardPaymentDetails,
  Guest,
  GuestDetails,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  Invoice,
  Item,
  ListAvailableTicketsOptions,
  ListAvailableTicketsRequest,
  ListAvailableTicketsResponse,
  ListOrdersOptions,
  ListOrdersRequest,
  ListOrdersResponse,
  MessageEnvelope,
  Money,
  OnlineConferencingLogin,
  Order,
  OrderConfirmed,
  OrderConfirmedEnvelope,
  OrderDeleted,
  OrderDeletedEnvelope,
  OrderFacetCounts,
  OrderFacets,
  OrderFieldset,
  OrderInitiated,
  OrderInitiatedEnvelope,
  OrderPageUrls,
  OrderPaid,
  OrderReservationCreatedEnvelope,
  OrderReservationUpdatedEnvelope,
  OrderStatus,
  OrderTag,
  OrderType,
  OrderUpdated,
  OrderUpdatedEnvelope,
  PaidPlanBenefit,
  PaidPlanDiscount,
  PaidPlanDiscountDiscountOneOf,
  PaymentDetails,
  PaymentTransaction,
  PaymentTransactionEvent,
  PaymentTransactionSummary,
  PercentDiscount,
  PosCheckoutOptions,
  PosCheckoutRequest,
  PosCheckoutResponse,
  PricingOption,
  PricingOptions,
  QueryAvailableTicketsOptions,
  QueryAvailableTicketsRequest,
  QueryAvailableTicketsResponse,
  QueryEventsWithPaidReservationsRequest,
  QueryEventsWithPaidReservationsResponse,
  RawHttpResponse,
  Reservation,
  ReservationCount,
  ReservationCreated,
  ReservationStatus,
  ReservationUpdated,
  ResponseMetaData,
  ScheduledActionEnumAction,
  StandardDetails,
  State,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  Tax,
  TaxType,
  Ticket,
  TicketDefinition,
  TicketDefinitionFieldset,
  TicketDetails,
  TicketPricing,
  TicketPricingPriceOneOf,
  TicketQuantity,
  TicketReservation,
  TicketReservationQuantity,
  TicketSalePeriod,
  TicketSaleStatus,
  TicketSales,
  TicketingTicket,
  Type,
  UpdateCheckoutOptions,
  UpdateCheckoutRequest,
  UpdateCheckoutResponse,
  UpdateOrderIdentifiers,
  UpdateOrderOptions,
  UpdateOrderRequest,
  UpdateOrderResponse,
  VoidAuthorizedPaymentOptions,
  VoidAuthorizedPaymentRequest,
  VoidAuthorizedPaymentResponse,
  WebhookIdentityType,
  WixFeeConfig,
} from './events-v1-order-orders.universal.js';
